import type { TaxRateLike } from '@/utils/nontaxDiffTooltip';
import type { CommonWageListRow } from '@/types/commonWage';

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, '')) || 0;
};

const fixRound10 = (value: number): number => Math.floor((Number(value) || 0) / 10) * 10;

const isRateBased = (amount: number, base: number, rate: number): boolean => {
  if (amount <= 0 || base <= 0 || !rate) return false;
  return Math.abs(fixRound10(base * rate) - amount) <= 10;
};

export interface CompanyInsuranceParts {
  c_yeon: number;
  c_gun: number;
  c_jang: number;
  c_go: number;
  c_sil: number;
  c_san: number;
}

export interface CompanyInsuranceResult {
  before: CompanyInsuranceParts;
  after: CompanyInsuranceParts;
  beforeTotal: number;
  afterTotal: number;
}

const sumCompanyIns = (parts: CompanyInsuranceParts): number =>
  parts.c_yeon + parts.c_gun + parts.c_jang + parts.c_go + parts.c_sil + parts.c_san;

/**
 * 비과세 미적용 시 기관부담 4대보험 재산출 (과세표준 × 요율, 10원 절사)
 */
export const calcBeforeCompanyInsuranceFromFacility = (
  beforeTaxable: number,
  afterTaxable: number,
  tax: TaxRateLike,
  wage: CommonWageListRow,
): CompanyInsuranceResult => {
  const isBoss = wage.cwhd_is_owner === 1;

  const after: CompanyInsuranceParts = {
    c_yeon: nc(wage.cwhd_c_yeon),
    c_gun: nc(wage.cwhd_c_gun),
    c_jang: nc(wage.cwhd_c_jang),
    c_go: nc(wage.cwhd_c_go),
    c_sil: nc(wage.cwhd_c_sil),
    c_san: nc(wage.cwhd_c_san),
  };

  const before: CompanyInsuranceParts = {
    c_yeon: after.c_yeon,
    c_gun: 0,
    c_jang: 0,
    c_go: 0,
    c_sil: 0,
    c_san: 0,
  };

  if (after.c_gun > 0 && isRateBased(after.c_gun, afterTaxable, tax.tr_gun)) {
    before.c_gun = fixRound10(beforeTaxable * tax.tr_gun);
    if (after.c_jang > 0) {
      if (isRateBased(after.c_jang, after.c_gun, tax.tr_jang)) {
        before.c_jang = fixRound10(before.c_gun * tax.tr_jang);
      } else {
        before.c_jang = after.c_jang;
      }
    }
  } else if (after.c_gun > 0) {
    before.c_gun = after.c_gun;
    before.c_jang = after.c_jang;
  }

  if (!isBoss && after.c_go > 0) {
    if (isRateBased(after.c_go, afterTaxable, tax.tr_go)) {
      before.c_go = fixRound10(beforeTaxable * tax.tr_go);
    } else {
      before.c_go = after.c_go;
    }
  }

  if (!isBoss && after.c_sil > 0) {
    if (isRateBased(after.c_sil, afterTaxable, tax.tr_sil)) {
      before.c_sil = fixRound10(beforeTaxable * tax.tr_sil);
    } else {
      before.c_sil = after.c_sil;
    }
  }

  if (!isBoss && after.c_san > 0) {
    if (isRateBased(after.c_san, afterTaxable, tax.tr_san)) {
      before.c_san = fixRound10(beforeTaxable * tax.tr_san);
    } else {
      before.c_san = after.c_san;
    }
  }

  return {
    before,
    after,
    beforeTotal: sumCompanyIns(before),
    afterTotal: sumCompanyIns(after),
  };
};
